/**
 * Hire Purchase — bikes riders are buying.
 *
 * The screen says "Collected" and "Outstanding" and never "Revenue", because
 * an instalment is not income. The company recognised the sale when it sold
 * the bike; every repayment after that reduces a receivable. The totals strip
 * labels it so a reader cannot mistake the two, and the API has no field here
 * that would let them.
 *
 * An agreement names a bike from the register. There is no field for a
 * registration on this screen on purpose — a plate typed twice is a plate that
 * can disagree with itself.
 */

import { useState, type ReactNode } from 'react';
import { Plus } from 'lucide-react';
import {
  HIRE_PURCHASE_STATUSES,
  HIRE_PURCHASE_STATUS_LABELS,
  INSTALMENT_FREQUENCIES,
  INSTALMENT_FREQUENCY_LABELS,
  type HirePurchaseStatus,
  type HirePurchaseView,
  type InstalmentFrequency,
} from '@cenox/shared';
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Input,
  Modal,
  Select,
  TableSkeleton,
  Textarea,
} from '../../components/ui';
import { useAuth } from '../../lib/auth';
import { money, todayIn } from '../../lib/format';
import { ApiError } from '../../lib/api';
import { useRiders } from '../logistics/data';
import { AgreementBadge, Progress, SectionTitle, Stat } from './parts';
import {
  useAgreements,
  useBikes,
  useCreateAgreement,
  useDeleteInstalment,
  useRecordInstalment,
  useUpdateAgreement,
} from './data';

const STATUS_OPTIONS = HIRE_PURCHASE_STATUSES.map((s) => ({
  value: s,
  label: HIRE_PURCHASE_STATUS_LABELS[s],
}));

const FREQUENCY_OPTIONS = INSTALMENT_FREQUENCIES.map((f) => ({
  value: f,
  label: INSTALMENT_FREQUENCY_LABELS[f],
}));

export function HirePurchasePage(): ReactNode {
  const { business, can } = useAuth();
  const currency = business?.currencyCode ?? 'NGN';
  const timezone = business?.timezone ?? 'Africa/Lagos';

  const [status, setStatus] = useState<HirePurchaseStatus | ''>('');
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState<HirePurchaseView | null>(null);

  const board = useAgreements(status ? { status } : {});
  const mayManage = can('hire_purchase:manage');

  if (board.isError) {
    return <ErrorState message="Could not load agreements." onRetry={() => void board.refetch()} />;
  }
  if (board.isPending) return <TableSkeleton rows={5} columns={6} />;

  const { agreements, totals } = board.data;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <Stat label="Agreements" count={totals.agreements} hint={`${totals.live} live`} />
        <Stat label="Financed" value={totals.financed} currency={currency} />
        <Stat
          label="Collected"
          value={totals.collected}
          currency={currency}
          tone="good"
          hint="Reduces a receivable"
        />
        <Stat
          label="Outstanding"
          value={totals.outstanding}
          currency={currency}
          tone="owed"
          hint="Owed on bikes, not revenue"
        />
        <Stat
          label="Behind schedule"
          count={totals.behindCount}
          tone={totals.behindCount > 0 ? 'warn' : 'plain'}
        />
      </div>

      <section>
        <SectionTitle
          aside={
            <div className="flex items-center gap-2">
              <Select
                value={status}
                onChange={(e) => setStatus(e.target.value as HirePurchaseStatus | '')}
                aria-label="Filter by status"
                placeholder="All statuses"
                options={STATUS_OPTIONS}
                className="h-9 py-0 text-sm"
              />
              {mayManage ? (
                <Button
                  size="sm"
                  icon={<Plus className="size-4" />}
                  onClick={() => setCreating(true)}
                >
                  New agreement
                </Button>
              ) : null}
            </div>
          }
        >
          Agreements
        </SectionTitle>

        {agreements.length === 0 ? (
          <EmptyState
            title="No hire purchase agreements"
            description="An agreement sells a bike from the register to a rider on instalments. Add the bike first, then write the agreement against it."
            action={
              mayManage ? (
                <Button onClick={() => setCreating(true)}>New agreement</Button>
              ) : undefined
            }
          />
        ) : (
          <Card>
            <div className="-mx-3 overflow-x-auto px-3">
              <table className="w-full min-w-[780px] text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                    <th className="py-2 pr-3 font-medium">Rider</th>
                    <th className="py-2 pr-3 font-medium">Bike</th>
                    <th className="py-2 pr-3 text-right font-medium">Price</th>
                    <th className="py-2 pr-3 text-right font-medium">Paid</th>
                    <th className="py-2 pr-3 text-right font-medium">Outstanding</th>
                    <th className="py-2 pr-3 font-medium">Progress</th>
                    <th className="py-2 pr-3 font-medium">Status</th>
                    <th className="py-2 font-medium" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {agreements.map((a) => (
                    <tr key={a.id}>
                      <td className="py-2 pr-3 font-medium text-slate-900">{a.riderName}</td>
                      <td className="py-2 pr-3 text-slate-600">
                        {a.bikeRegistration}
                        {a.bikeModel ? (
                          <span className="block text-xs text-slate-400">{a.bikeModel}</span>
                        ) : null}
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums text-slate-900">
                        {money(a.sellingPrice, currency)}
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums text-emerald-700">
                        {money(a.amountPaid, currency)}
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums text-slate-900">
                        {money(a.outstanding, currency)}
                      </td>
                      <td className="py-2 pr-3">
                        <div className="w-28">
                          <Progress percent={a.percentPaid} />
                          <p className="mt-1 text-xs text-slate-500">
                            {a.instalmentsPaid}/{a.instalmentsExpected}
                            {a.instalmentsBehind > 0 ? (
                              <span className="text-amber-700"> · {a.instalmentsBehind} behind</span>
                            ) : null}
                          </p>
                        </div>
                      </td>
                      <td className="py-2 pr-3">
                        <AgreementBadge status={a.status} />
                      </td>
                      <td className="py-2 text-right">
                        <Button variant="ghost" size="sm" onClick={() => setOpen(a)}>
                          Open
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </section>

      {creating ? (
        <NewAgreementDialog
          currency={currency}
          today={todayIn(timezone)}
          onClose={() => setCreating(false)}
        />
      ) : null}

      {open ? (
        <AgreementDialog
          agreement={open}
          currency={currency}
          today={todayIn(timezone)}
          mayManage={mayManage}
          onClose={() => setOpen(null)}
        />
      ) : null}
    </div>
  );
}

/**
 * Write an agreement.
 *
 * The bike list is the register, filtered to what can actually be sold: a bike
 * already carrying a live agreement is not offered, because only one may be
 * live at a time and discovering that on submit wastes the typing.
 */
function NewAgreementDialog({
  currency,
  today,
  onClose,
}: {
  currency: string;
  today: string;
  onClose: () => void;
}): ReactNode {
  const bikes = useBikes();
  const riders = useRiders();
  const create = useCreateAgreement();

  const [bikeId, setBikeId] = useState('');
  const [riderId, setRiderId] = useState('');
  const [bikeCost, setBikeCost] = useState('');
  const [sellingPrice, setSellingPrice] = useState('');
  const [deposit, setDeposit] = useState('0');
  const [instalmentAmount, setInstalmentAmount] = useState('');
  const [instalmentFrequency, setInstalmentFrequency] = useState<InstalmentFrequency>('weekly');
  const [startsOn, setStartsOn] = useState(today);
  const [note, setNote] = useState('');

  const fields = create.error instanceof ApiError ? create.error.fields : undefined;
  const available = (bikes.data?.bikes ?? []).filter(
    (b) => b.agreementId === null && b.status !== 'sold',
  );

  /*
    The cost defaults from the register, so the margin is right without
    retyping — and stays editable, because a bike sold two years after it was
    bought is not being sold at its acquisition cost.
  */
  const pickBike = (id: string) => {
    setBikeId(id);
    const bike = available.find((b) => b.id === id);
    if (bike?.acquisitionCost && bikeCost === '') setBikeCost(bike.acquisitionCost);
    if (bike?.riderId && riderId === '') setRiderId(bike.riderId);
  };

  const financed = Math.max(0, Number(sellingPrice || 0) - Number(deposit || 0));

  return (
    <Modal open onClose={onClose} title="New hire purchase agreement" size="lg">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate(
            {
              bikeId,
              riderId,
              bikeCost,
              sellingPrice,
              deposit: deposit || '0',
              instalmentAmount,
              instalmentFrequency,
              startsOn,
              ...(note ? { note } : {}),
            },
            { onSuccess: onClose },
          );
        }}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label="Bike"
            error={fields?.bikeId?.[0]}
            required
            value={bikeId}
            onChange={(e) => pickBike(e.target.value)}
            placeholder="Choose a bike…"
            options={available.map((b) => ({
              value: b.id,
              label: b.model ? `${b.registration} — ${b.model}` : b.registration,
            }))}
          />

          <Select
            label="Rider"
            error={fields?.riderId?.[0]}
            required
            value={riderId}
            onChange={(e) => setRiderId(e.target.value)}
            placeholder="Choose a rider…"
            options={(riders.data?.data ?? []).map((r) => ({
              value: r.id,
              label: r.canonicalName,
            }))}
          />

          <Input
            label="What the bike cost"
            error={fields?.bikeCost?.[0]}
            hint="Kept so the margin on the sale stays visible"
            required
            value={bikeCost}
            onChange={(e) => setBikeCost(e.target.value)}
            inputMode="decimal"
          />

          <Input
            label="Selling price"
            error={fields?.sellingPrice?.[0]}
            required
            value={sellingPrice}
            onChange={(e) => setSellingPrice(e.target.value)}
            inputMode="decimal"
          />

          <Input
            label="Deposit"
            error={fields?.deposit?.[0]}
            value={deposit}
            onChange={(e) => setDeposit(e.target.value)}
            inputMode="decimal"
          />

          <Input
            label="Instalment"
            error={fields?.instalmentAmount?.[0]}
            required
            value={instalmentAmount}
            onChange={(e) => setInstalmentAmount(e.target.value)}
            inputMode="decimal"
          />

          <Select
            label="How often"
            error={fields?.instalmentFrequency?.[0]}
            value={instalmentFrequency}
            onChange={(e) => setInstalmentFrequency(e.target.value as InstalmentFrequency)}
            options={FREQUENCY_OPTIONS}
          />

          <Input
            label="Starts on"
            error={fields?.startsOn?.[0]}
            required
            type="date"
            value={startsOn}
            onChange={(e) => setStartsOn(e.target.value)}
          />
        </div>

        {sellingPrice ? (
          <p className="text-sm text-slate-600">
            Financing {money(String(financed), currency)}
            {bikeCost
              ? ` · margin ${money(String(Number(sellingPrice) - Number(bikeCost)), currency)}`
              : ''}
          </p>
        ) : null}

        <Textarea
          label="Note"
          error={fields?.note?.[0]}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
        />

        {create.error && !fields ? <ErrorState message={create.error.message} /> : null}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={create.isPending}>
            Create agreement
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** One agreement: its terms, its instalments, and the next one to record. */
function AgreementDialog({
  agreement,
  currency,
  today,
  mayManage,
  onClose,
}: {
  agreement: HirePurchaseView;
  currency: string;
  today: string;
  mayManage: boolean;
  onClose: () => void;
}): ReactNode {
  const [amount, setAmount] = useState(agreement.instalmentAmount);
  const [paidOn, setPaidOn] = useState(today);
  const [method, setMethod] = useState('Cash');

  const record = useRecordInstalment();
  const reverse = useDeleteInstalment();
  const update = useUpdateAgreement();
  const error = record.error ?? reverse.error ?? update.error;
  const fields = error instanceof ApiError ? error.fields : undefined;

  return (
    <Modal
      open
      onClose={onClose}
      title={`${agreement.bikeRegistration} — ${agreement.riderName}`}
      size="lg"
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label="Price" value={agreement.sellingPrice} currency={currency} />
          <Stat label="Deposit" value={agreement.deposit} currency={currency} />
          <Stat label="Paid" value={agreement.amountPaid} currency={currency} tone="good" />
          <Stat label="Outstanding" value={agreement.outstanding} currency={currency} tone="owed" />
        </div>

        <div>
          <Progress percent={agreement.percentPaid} />
          <p className="mt-1 text-xs text-slate-500">
            {agreement.percentPaid}% paid · {money(agreement.instalmentAmount, currency)}{' '}
            {agreement.instalmentFrequency} · started {agreement.startsOn}
            {agreement.expectedCompletionOn
              ? ` · due to finish ${agreement.expectedCompletionOn}`
              : ''}
          </p>
        </div>

        {mayManage && agreement.status !== 'completed' && Number(agreement.outstanding) > 0 ? (
          <form
            className="space-y-3 rounded-lg border border-slate-200 p-3"
            onSubmit={(e) => {
              e.preventDefault();
              record.mutate(
                { id: agreement.id, input: { amount, paidOn, ...(method ? { method } : {}) } },
                { onSuccess: onClose },
              );
            }}
          >
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Record an instalment
            </p>
            <div className="grid gap-3 sm:grid-cols-3">
              <Input
                label="Amount"
                error={fields?.amount?.[0]}
                required
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                inputMode="decimal"
              />
              <Input
                label="Received on"
                error={fields?.paidOn?.[0]}
                required
                type="date"
                value={paidOn}
                min={agreement.startsOn}
                onChange={(e) => setPaidOn(e.target.value)}
              />
              <Input
                label="How"
                error={fields?.method?.[0]}
                value={method}
                onChange={(e) => setMethod(e.target.value)}
              />
            </div>
            <p className="text-xs text-slate-400">
              This reduces what the rider owes on the bike. It is not remittance and not revenue.
            </p>
            <div className="flex justify-end">
              <Button type="submit" size="sm" disabled={record.isPending}>
                Record instalment
              </Button>
            </div>
          </form>
        ) : null}

        {error && !fields ? <ErrorState message={error.message} /> : null}

        <div>
          <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-slate-500">
            Instalments received
          </p>
          {agreement.payments.length === 0 ? (
            <p className="text-sm text-slate-500">Nothing received yet.</p>
          ) : (
            <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
              {agreement.payments.map((p) => (
                <li key={p.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                  <span className="tabular-nums text-slate-900">{money(p.amount, currency)}</span>
                  <span className="text-xs text-slate-500">
                    {p.paidOn}
                    {p.method ? ` · ${p.method}` : ''}
                    {p.recordedBy ? ` · ${p.recordedBy}` : ''}
                  </span>
                  {mayManage ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => reverse.mutate(p.id, { onSuccess: onClose })}
                      disabled={reverse.isPending}
                    >
                      Reverse
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex flex-wrap items-end justify-between gap-2 border-t border-slate-200 pt-3">
          {mayManage ? (
            <div className="w-full max-w-[200px]">
              <Select
                label="Status"
                value={agreement.status}
                onChange={(e) =>
                  update.mutate({
                    id: agreement.id,
                    input: { status: e.target.value as HirePurchaseStatus },
                  })
                }
                options={STATUS_OPTIONS}
              />
            </div>
          ) : (
            <span />
          )}
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
}
